import { MessageCircle, Star, X, Check } from "lucide-react";
import { waLink, WA_MESSAGES } from "@/lib/whatsapp";

const HERO_IMG = "/ai/hero-yoga.jpg";
const AVATAR_SRCS = [
  "https://images.unsplash.com/photo-1770838447151-05a876cdee3d",
  "https://images.unsplash.com/photo-1749189516333-168cfd97de0b",
  "https://images.unsplash.com/photo-1713078582993-fdd86b1a2c1f",
];
const NO_BENEFITS = ["No Gym / Expensive Equipment", "No Calorie-Restrictive Diets", "No Daily Workouts"];
const YES_BENEFITS = ["Burn Calories 3× Faster", "Automate Fat-Burning 24/7", "7-Day Money-Back Guarantee"];

export default function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="container-x pt-6 md:pt-10 pb-10 md:pb-14 grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
        <div className="animate-fade-up order-2 lg:order-1">
          <h1 className="font-serif text-[36px] leading-[1.04] sm:text-5xl md:text-6xl lg:text-7xl tracking-tight text-forest">
            Ancient Wisdom <span className="italic text-clay">–</span>{" "}
            <em className="italic text-clay">Real Transformation</em>
          </h1>
          <p className="mt-5 md:mt-6 text-base md:text-lg text-ink-soft leading-relaxed max-w-xl">
            A proven, beginner-friendly system — science-backed and habit-driven, designed to help you: burn fat, look toned and athletic, maximise energy and build sky-high confidence. 1-on-1 personalised live coaching - from the comfort of your home.
          </p>

          <div className="mt-7 md:mt-8 flex flex-wrap gap-3">
            <a
              href={waLink(WA_MESSAGES.trial)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-consultation-cta"
              className="btn-accent w-full sm:w-auto"
            >
              <MessageCircle className="h-4 w-4" /> Book Your Free Consultation
            </a>
            <a href="#programs" data-testid="hero-explore-cta" className="btn-outline w-full sm:w-auto">
              Explore programs
            </a>
          </div>

          <div className="mt-7 md:mt-8 grid sm:grid-cols-2 gap-4 sm:gap-6">
            <BenefitBlock variant="no" items={NO_BENEFITS} />
            <BenefitBlock variant="yes" items={YES_BENEFITS} />
          </div>
        </div>

        <div className="relative animate-fade-up order-1 lg:order-2">
          <div className="absolute -top-6 -left-6 h-48 w-48 md:h-64 md:w-64 rounded-full bg-clay/20 blur-3xl" />
          <div className="absolute -bottom-6 -right-6 h-56 w-56 md:h-72 md:w-72 rounded-full bg-forest/10 blur-3xl" />
          <div className="relative rounded-[2rem] md:rounded-[2.5rem] overflow-hidden aspect-[4/5] border border-sand-300 shadow-2xl bg-sand-200">
            <img
              src={HERO_IMG}
              alt="Online yoga at home — calm, focused practice with a certified guru"
              className="h-full w-full object-cover"
              loading="eager"
              fetchpriority="high"
            />
            <div className="absolute inset-x-3 bottom-3 sm:inset-x-4 sm:bottom-4 md:inset-x-5 md:bottom-5 rounded-2xl bg-white/95 backdrop-blur-md p-3 sm:p-4 md:p-5 border border-sand-300">
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2">
                  {AVATAR_SRCS.map((src) => (
                    <img key={src} src={src} alt="" className="h-7 w-7 sm:h-8 sm:w-8 md:h-9 md:w-9 rounded-full object-cover border-2 border-white" />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1 text-clay">
                    {[...Array(5)].map((_, i) => <Star key={`star-${i}`} className="h-3.5 w-3.5 fill-current" />)}
                  </div>
                  <p className="text-[11px] sm:text-xs text-ink-soft mt-0.5">Rated 4.9 by 1,000+ clients</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BenefitBlock({ variant, items }) {
  const isYes = variant === "yes";
  const Icon = isYes ? Check : X;
  const colorClass = isYes ? "text-forest" : "text-clay";
  return (
    <ul className="space-y-2.5">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-2.5 text-sm text-ink-soft">
          <span className={`grid h-5 w-5 place-items-center rounded-full shrink-0 mt-0.5 ${isYes ? "bg-forest/10" : "bg-clay/10"}`}>
            <Icon className={`h-3 w-3 ${colorClass}`} strokeWidth={3} />
          </span>
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}
